import React from 'react';
import { Button, Col, FormLabel, Row } from 'react-bootstrap';
import InputCell from './InputCell';
import { Plus } from 'react-bootstrap-icons';

const getSubFieldColBreakpoints = subField => ({
  xs: subField.cellSize || 3,
  ...(subField.cellSizeSm != null ? { sm: subField.cellSizeSm } : {}),
  ...(subField.cellSizeMd != null ? { md: subField.cellSizeMd } : {}),
  ...(subField.cellSizeLg != null ? { lg: subField.cellSizeLg } : {}),
  ...(subField.cellSizeXl != null ? { xl: subField.cellSizeXl } : {})
});

const entriesStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 18
};

const entryStyle = {
  border: '1px solid #e3e6ea',
  borderRadius: 6,
  padding: '8px 10px 10px',
  backgroundColor: '#fcfcfd'
};

const entryRemoveTopRightStyle = {
  ...entryStyle,
  position: 'relative',
  paddingRight: 48
};

const removeTopStyle = {
  position: 'absolute',
  top: 8,
  right: 8,
  zIndex: 1
};

const MultiFieldInput = ({
  field,
  formValues,
  onChange,
  handleDeleteSubField,
  handleAddSubField,
  enableFieldStatus = false,
  fieldStatuses = null
}) => {
  const resolveFieldTone = fieldKey =>
    enableFieldStatus && fieldStatuses && fieldKey ? fieldStatuses[fieldKey] ?? null : null;

  // Invoice rows use minWidth; unit/condo rows shrink and keep X on the same line.
  const hasFixedWidthFields = field.fields?.some(subField => subField.minWidth != null);
  const removeTopRight = field.removeButtonPosition === 'top-right';

  const colProps = {
    className: field.keepColumnPadding ? undefined : field.className || 'px-0',
    xs: field.cellSize ?? 12,
    ...(field.cellSizeLg != null ? { lg: field.cellSizeLg } : {})
  };

  const rowClassName = hasFixedWidthFields
    ? 'align-items-end mx-0'
    : 'col-12 pr-0 flex-nowrap align-items-end mb-2';

  const renderRemoveButton = index => (
    <Button
      variant="outline-danger"
      className="mx-1"
      onClick={() => handleDeleteSubField(field.key, index)}
    >
      X
    </Button>
  );

  const renderEntry = (subFieldData, index) => {
    const row = (
      <Row className={rowClassName}>
        {field.fields.map(subField => {
          const minWidth = subField.minWidth;
          return (
            <Col
              {...getSubFieldColBreakpoints(subField)}
              className="px-1"
              key={subField.key + index}
              style={
                minWidth != null
                  ? { minWidth, flexShrink: 0 }
                  : { minWidth: 0 }
              }
            >
              <InputCell
                field={subField}
                formValues={subFieldData}
                onChange={e => {
                  const value = e.target ? e.target.value : e;
                  onChange(field.key, subField.key, value, index);
                }}
                enableFieldStatus={enableFieldStatus}
                fieldTone={resolveFieldTone(subField.key)}
              />
            </Col>
          );
        })}
        {!removeTopRight && (
          <Col
            xs="auto"
            className={`d-flex align-items-end justify-content-start${
              hasFixedWidthFields ? ' pl-2' : ' px-1'
            }`}
            style={{ flexShrink: 0 }}
          >
            {renderRemoveButton(index)}
          </Col>
        )}
      </Row>
    );

    if (!hasFixedWidthFields) {
      return <React.Fragment key={index}>{row}</React.Fragment>;
    }

    return (
      <div
        key={index}
        style={removeTopRight ? entryRemoveTopRightStyle : entryStyle}
      >
        {removeTopRight && <div style={removeTopStyle}>{renderRemoveButton(index)}</div>}
        {row}
      </div>
    );
  };

  return (
    <Col {...colProps}>
      <div className="d-flex flex-column">
        {!field.hideLabel && <FormLabel className="midFont mt-2">{field.label}</FormLabel>}
        {formValues[field.key]?.length > 0 && (
          <div
            className="mb-3"
            style={hasFixedWidthFields ? entriesStyle : undefined}
          >
            {formValues[field.key].map((subFieldData, index) => renderEntry(subFieldData, index))}
          </div>
        )}
        <Button
          variant="outline-dark"
          className="align-self-start my-2 d-flex align-items-center"
          size="sm"
          onClick={() => handleAddSubField(field.key)}
        >
          <Plus size={24} />
          {formValues[field.key]?.length === 0 && <span className="ml-2">Click To Add</span>}
        </Button>
      </div>
    </Col>
  );
};

export default MultiFieldInput;
